import * as THREE from 'three'
import Experience from '../../Experience'
import { InteractableMixin } from '../../Utils/InteractableMixin'
import UfoFlyby from './UfoFlyby'
import ScreenCrack from './ScreenCrack'

/**
 * Satellite — 惑星上空に浮かぶ人工衛星のプロップ。
 * 空中に自由配置（表面スナップなし）。位置・回転・スケールは debug GUI で調整する。
 * タップすると UFO が空を横切る（UfoFlyby）。
 */
export default class Satellite {
  constructor() {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.time = this.experience.time
    this.debug = this.experience.debug

    // 空中に浮かんでいる感じのゆったりした揺れ（Book の floatParams と同じ仕組み）
    this.floatParams = { amplitude: 0.15, speed: 0.4 }

    // ジェットパック衝突時の「画面のガラスが割れる」フィルター
    this.screenCrack = new ScreenCrack()

    // タップで飛ぶ UFO たち（待機中の機体からランダムに1機発射する）
    this.ufos = [
      // 緑の円盤: 途中でプレイヤーに気づき、目の前まで来て覗き込んでから飛び去る
      new UfoFlyby('ufoGreenSpaceship', {
        pathType: 'peekAtCamera',
        duration: 10,
        // 後ずさり開始（t=0.8）に合わせて飛び去り音
        soundCues: [{ t: 0.8, name: 'ufoUpSE', volume: 0.6 }],
      }),
      // ジェットパックの宇宙人: 制御不能でクルクル回転しながら画面に突撃 → ひび割れ → ずり落ちる
      // （+Z 向きのモデルなので進行方向 +X へ向ける）
      new UfoFlyby('ufoJetpackGo', {
        modelYaw: Math.PI / 2,
        pathType: 'crashIntoCamera',
        onImpact: (screenPos) => {
          this.experience.audioManager?.playSE('ufoRockBreakSE', 0.7)
          // カメラが動いていても、ひび割れは必ず画面内（中央寄り）に出す
          const clamp = (v) => Math.min(75, Math.max(25, v))
          this.screenCrack.show(clamp(screenPos.x), clamp(screenPos.y))
        },
      }),
      // 円盤UFO: 後半でカメラ前を掠める迫力のフライバイ + スピン
      new UfoFlyby('ufoFlyingSaucer', {
        pathType: 'closeFlyby',
        size: 1.6,
        spin: 1.5,
        // 離脱フェーズ開始（swoop 0.85 ≒ t 0.88）に合わせてワープ音
        soundCues: [{ t: 0.88, name: 'ufoWarpSE', volume: 0.6 }],
      }),
    ]

    this.setModel()
    this.setInteraction()
    this.setDebug()
  }

  // 待機中の UFO からランダムに1機発射（全機飛行中なら何もしない）
  launchRandomUfo() {
    const idleUfos = this.ufos.filter((ufo) => !ufo.isFlying)
    if (idleUfos.length === 0) return
    idleUfos[Math.floor(Math.random() * idleUfos.length)].launch()
  }

  setModel() {
    // group（ワールド配置・GUI調整）→ floatGroup（浮遊の揺れ）→ モデル/ヒットスフィア
    this.group = new THREE.Group()
    this.floatGroup = new THREE.Group()
    this.group.add(this.floatGroup)
    this.model = this.experience.resources.items.satelliteModel.scene

    // 原寸が不明なので最大辺 1.0 ユニットに正規化し、モデル中心をグループ原点に合わせる
    // （空中で自由回転させるため、telescope と違い足元ではなく中心を原点にする）
    const bounds = new THREE.Box3().setFromObject(this.model)
    const size = bounds.getSize(new THREE.Vector3())
    const modelScale = 1.0 / Math.max(size.x, size.y, size.z)
    this.model.scale.setScalar(modelScale)
    const center = bounds.getCenter(new THREE.Vector3())
    this.model.position.copy(center).multiplyScalar(-modelScale)
    this.floatGroup.add(this.model)

    // レイキャスト用の不可視ヒットスフィア（InteractionManager は this.mesh のみ判定する）
    this.mesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.6, 8, 8),
      new THREE.MeshBasicMaterial({ visible: false })
    )
    this.floatGroup.add(this.mesh)

    // 初期配置（debug GUI で調整した値）
    this.group.position.set(-4.9, -2.3, 19.4)
    this.group.rotation.set(0.1284, 2.4484, -0.4915)
    this.group.scale.setScalar(0.95)

    this.scene.add(this.group)
  }

  update() {
    // ゆったりとした浮遊: 上下のたゆたい + わずかな傾きの揺れ
    const elapsed = this.time.elapsed
    const { amplitude, speed } = this.floatParams
    this.floatGroup.position.y = Math.sin(elapsed * speed) * amplitude
    this.floatGroup.rotation.z = Math.sin(elapsed * speed * 0.7) * 0.05
    this.floatGroup.rotation.x = Math.sin(elapsed * speed * 0.5) * 0.04
  }

  setInteraction() {
    Object.assign(this, InteractableMixin)

    this.onInteract = () => {
      this.experience.audioManager?.playSE('satelliteOutgoingSE', 0.6)
      this.launchRandomUfo()
    }

    this.makeInteractable()
  }

  setDebug() {
    if (!this.debug.active) return

    this.debugFolder = this.debug.ui.addFolder('Satellite')
    this.debugFolder.add(this.group.position, 'x').min(-15).max(15).step(0.05).name('Position X')
    this.debugFolder.add(this.group.position, 'y').min(-10).max(10).step(0.05).name('Position Y')
    this.debugFolder.add(this.group.position, 'z').min(0).max(28).step(0.05).name('Position Z')
    this.debugFolder.add(this.group.rotation, 'x').min(-Math.PI).max(Math.PI).step(0.01).name('Rotation X')
    this.debugFolder.add(this.group.rotation, 'y').min(-Math.PI).max(Math.PI).step(0.01).name('Rotation Y')
    this.debugFolder.add(this.group.rotation, 'z').min(-Math.PI).max(Math.PI).step(0.01).name('Rotation Z')
    this.debugFolder.add(this.group.scale, 'x').min(0.1).max(3).step(0.05).name('Scale')
      .onChange((value) => this.group.scale.setScalar(value))
    this.debugFolder.add(this.floatParams, 'amplitude').min(0).max(0.5).step(0.01).name('Float Amplitude')
    this.debugFolder.add(this.floatParams, 'speed').min(0).max(2).step(0.05).name('Float Speed')

    // UFO 演出の調整（飛行パラメータのスライダーは全機に一括反映）
    this.debugFolder.add({ launch: () => this.launchRandomUfo() }, 'launch').name('🛸 Launch Random UFO')
    this.debugFolder.add({ launch: () => this.ufos[0].launch() }, 'launch').name('🛸 Launch Green Ship')
    this.debugFolder.add({ launch: () => this.ufos[1].launch() }, 'launch').name('🛸 Launch Jetpack')
    this.debugFolder.add({ launch: () => this.ufos[2].launch() }, 'launch').name('🛸 Launch Saucer')
    this.debugFolder.add({ crack: () => this.screenCrack.show(55, 48) }, 'crack').name('💥 Test Screen Crack')

    const applyToAllUfos = (key) => (value) => this.ufos.forEach((ufo) => { ufo[key] = value })
    this.debugFolder.add(this.ufos[0], 'baseY').min(-2).max(8).step(0.1).name('UFO Base Y')
      .onChange(applyToAllUfos('baseY'))
    this.debugFolder.add(this.ufos[0], 'depth').min(4).max(24).step(0.5).name('UFO Depth (z)')
      .onChange(applyToAllUfos('depth'))
    this.debugFolder.add(this.ufos[0], 'waveAmp').min(0).max(3).step(0.05).name('UFO Wave Amp')
      .onChange(applyToAllUfos('waveAmp'))
    this.debugFolder.add(this.ufos[0], 'duration').min(3).max(20).step(0.5).name('UFO Duration (s)')
      .onChange(applyToAllUfos('duration'))
    this.debugFolder.close()
  }
}
